import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  InputAdornment, Pagination, Badge
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  LocalBar as BarIcon,
  Restaurant as RestaurantIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  Assignment as AssignmentIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface AssignmentRecord {
  id: number;
  date: string;
  waiterId: string;
  waiterName: string;
  department: 'bar' | 'restaurant';
  details: string;
  status: 'assigned' | 'completed' | 'cancelled';
  supervisorId: string;
  createdBy: string;
  createdAt: string;
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  waiterId: string;
  department: 'all' | 'bar' | 'restaurant';
  status: 'all' | 'assigned' | 'completed' | 'cancelled';
  search: string;
}

interface Waiter {
  user_id: string;
  fullName: string;
}

const AssignmentHistory: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const [records, setRecords] = useState<AssignmentRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<AssignmentRecord[]>([]);
  const [waiters, setWaiters] = useState<Array<{id: string, name: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)), // Last 7 days
    endDate: new Date(),
    waiterId: 'all',
    department: 'all',
    status: 'all',
    search: ''
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch waiters for filter dropdown
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      console.log("Waiters response:", response.data);
      
      if (response.data.status) {
        const waiterList = (response.data.data || []).map((w: any) => ({
          id: w.user_id,
          name: w.fullName
        }));
        setWaiters(waiterList);
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch assignment history
  const fetchAssignmentHistory = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      // Add date filters
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getAssignmentHistory`, formData, config);
      console.log("Assignment history response:", response.data);

      // Handle the nested data structure
      if (response.data.status && response.data.data?.data) {
        const mappedRecords = response.data.data.data.map((item: any) => ({
          id: item.id,
          date: item.date,
          waiterId: item.waiterId,
          waiterName: item.waiterName,
          department: item.department,
          details: item.details || 'No notes',
          status: item.status,
          supervisorId: item.supervisorId,
          createdBy: item.createdBy,
          createdAt: item.createdAt
        }));
        
        setRecords(mappedRecords);
        applyFilters(mappedRecords);
      } else {
        setRecords([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      console.error("Fetch assignment error:", error);
      showNotification('Failed to fetch assignment history', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = (data: AssignmentRecord[]) => {
    let filtered = [...data];

    // Filter by waiter
    if (filters.waiterId !== 'all') {
      filtered = filtered.filter(r => r.waiterId === filters.waiterId);
    }

    // Filter by department
    if (filters.department !== 'all') {
      filtered = filtered.filter(r => r.department === filters.department);
    }

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(r => r.status === filters.status);
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(r => 
        r.waiterName?.toLowerCase().includes(searchLower) ||
        r.details?.toLowerCase().includes(searchLower) ||
        r.status?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredRecords(filtered);
    setPage(1); // Reset to first page when filters change
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters when filters change
  useEffect(() => {
    applyFilters(records);
  }, [filters.waiterId, filters.department, filters.status, filters.search, records]);

  // Fetch data when date range changes
  useEffect(() => {
    if (waiters.length > 0) {
      fetchAssignmentHistory();
    }
  }, [filters.startDate, filters.endDate]);

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchWaiters();
    };
    loadInitialData();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date(),
      waiterId: 'all',
      department: 'all',
      status: 'all',
      search: ''
    });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAssignmentHistory().finally(() => setRefreshing(false));
  };

  const getDepartmentIcon = (dept: string) => {
    return dept === 'bar' ? <BarIcon /> : <RestaurantIcon />;
  };

  const getDepartmentColor = (dept: string) => {
    return dept === 'bar' ? '#3b82f6' : '#f97316';
  };

  const getStatusChip = (record: AssignmentRecord) => {
    const statusColors: Record<string, string> = {
      assigned: '#f59e0b',
      completed: '#10b981',
      cancelled: '#ef4444'
    };
    return (
      <Chip
        size="small"
        label={record.status}
        sx={{
          bgcolor: alpha(statusColors[record.status] || '#6b7280', 0.1),
          color: statusColors[record.status] || '#6b7280',
          textTransform: 'capitalize'
        }}
      />
    );
  };

  const exportToCSV = () => {
    try {
      const headers = ['Date', 'Waiter', 'Department', 'Details', 'Status', 'Assigned On', 'Assigned By'];
      const csvData = filteredRecords.map(assignment => [
        new Date(assignment.date).toLocaleDateString(),
        assignment.waiterName || '',
        assignment.department === 'bar' ? 'Bar' : 'Restaurant',
        assignment.details || '-',
        assignment.status,
        new Date(assignment.createdAt).toLocaleString(),
        assignment.createdBy || '-'
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `assignment_history_${formatDate(new Date())}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  // Pagination
  const pageCount = Math.ceil(filteredRecords.length / rowsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getDateRangeLabel = () => {
    if (filters.startDate && filters.endDate) {
      return `${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
    }
    return 'Select date range';
  };

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main
                    }}
                  >
                    <HistoryIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Assignment History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View waiter assignment history
                    </Typography>
                  </Box>
                  <Chip 
                    label={getDateRangeLabel()}
                    icon={<DateRangeIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Export to CSV">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={filteredRecords.length === 0}
                        size="small"
                      >
                        Export
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshing}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Filters */}
              <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Waiter</InputLabel>
                            <Select
                              value={filters.waiterId}
                              label="Waiter"
                              onChange={(e) => handleFilterChange('waiterId', e.target.value)}
                            >
                              <MenuItem value="all">All Waiters</MenuItem>
                              {waiters.map(waiter => (
                                <MenuItem key={waiter.id} value={waiter.id}>
                                  {waiter.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Department</InputLabel>
                            <Select
                              value={filters.department}
                              label="Department"
                              onChange={(e) => handleFilterChange('department', e.target.value)}
                            >
                              <MenuItem value="all">All Departments</MenuItem>
                              <MenuItem value="bar">Bar</MenuItem>
                              <MenuItem value="restaurant">Restaurant</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filters.status}
                              label="Status"
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                              <MenuItem value="all">All Statuses</MenuItem>
                              <MenuItem value="assigned">Assigned</MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search by waiter name or details"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleFilterChange('search', '')}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {filteredRecords.length} assignment records
                        </Typography>
                        <Button
                          size="small"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* History Table */}
              <Paper>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Waiter</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned On</TableCell>
                        <TableCell>Assigned By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : paginatedRecords.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                            <Stack spacing={2} alignItems="center">
                              <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                              <Typography color="text.secondary">No assignment records found</Typography>
                              <Button variant="outlined" size="small" onClick={clearFilters}>
                                Clear Filters
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedRecords.map((record) => (
                          <TableRow key={`assignment-${record.id}`} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(record.date).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                  {record.waiterName?.charAt(0) || '?'}
                                </Avatar>
                                <Typography variant="body2">
                                  {record.waiterName || 'Unknown'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getDepartmentIcon(record.department)}
                                label={record.department === 'bar' ? 'Bar' : 'Restaurant'}
                                size="small"
                                sx={{
                                  bgcolor: alpha(getDepartmentColor(record.department), 0.1),
                                  color: getDepartmentColor(record.department)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                                {record.details || 'No notes'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {getStatusChip(record)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(record.createdAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {record.createdBy || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredRecords.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Paper>
            </Container>

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default AssignmentHistory;